/**
 * Per-project rights matrix — endpoint contract and pure helpers.
 *
 * The matrix is NOT entity data: it is the group permissions of the project,
 * served by /api/project/{id}/rights, whose axes (roles × task types) are
 * resolved server-side. See \Drupal\orbit_group\Service\ProjectRightsMatrix.
 *
 * Lives outside the component file so the React module keeps a single
 * component export (Fast Refresh requirement).
 */

/** Matrix columns. The keys are the contract of the endpoint. */
export const GENERAL_COLUMNS = [
  { key: 'timeVisibility', label: 'Visibilité des temps' },
  { key: 'timeLog', label: 'Log de temps' },
];

// Pas de colonne « Visibilité » : voir un ticket n'est jamais refusé à un
// membre du projet, quel que soit son rôle. La matrice ne retire que le droit
// d'AGIR sur un type de tâche (ou de lire ses commentaires).
export const TASK_COLUMNS = [
  { key: 'creation', label: 'Création' },
  { key: 'modification', label: 'Modification' },
  { key: 'deletion', label: 'Suppression' },
  { key: 'commentVisibility', label: 'Com. Visible' },
];

/** GET the matrix of a project (or of a project template). Rejects with the HTTP status. */
export async function fetchProjectRights(projectId) {
  const res = await fetch(`/api/project/${projectId}/rights`, { credentials: 'include' });
  if (!res.ok) throw res.status;
  return res.json();
}

/** POST the matrix back. Resolves with the server's fresh copy. */
export async function saveProjectRights(projectId, rights) {
  const res = await fetch(`/api/project/${projectId}/rights`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ general: rights.general, tasks: rights.tasks }),
  });
  if (!res.ok) throw res.status;
  return res.json();
}

/** Flips a general right, returning the next matrix (never mutates). */
export function toggleGeneralRight(rights, roleId, column) {
  return {
    ...rights,
    general: {
      ...rights.general,
      [roleId]: { ...rights.general?.[roleId], [column]: !(rights.general?.[roleId]?.[column] ?? true) },
    },
  };
}

/**
 * Flips a per-task-type right, returning the next matrix.
 *
 * Chaque colonne est indépendante : il n'y a plus de « Visibilité » qui
 * commandait la ligne, puisque voir un ticket ne se refuse plus.
 */
export function toggleTaskRight(rights, roleId, tid, column) {
  const row = rights.tasks?.[roleId]?.[tid] || {};
  const updated = { ...row, [column]: !(row[column] ?? true) };
  return {
    ...rights,
    tasks: { ...rights.tasks, [roleId]: { ...rights.tasks?.[roleId], [tid]: updated } },
  };
}
